import CancelIcon from "@mui/icons-material/Cancel";
import * as React from "react";

import { AlertDialog, RobotoButton } from "@/shared/components";
import { useActiveInvocationCount } from "@/shared/domain/actions/hooks";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { useCancelInvocationsByDataset } from "./useCancelInvocationsByDataset";

export interface CancelAllProps {
  datasetId?: string;
}

/**
 * A button to cancel all currently active invocations associated with a dataset.
 * Automatically polls for active invocations and only renders if there are active invocations to cancel.
 */
export function CancelAll({ datasetId }: CancelAllProps) {
  const [alertDialogOpen, setAlertDialogOpen] = React.useState(false);
  const cancelInvocationsMutation = useCancelInvocationsByDataset();
  const { data: activeCount = 0, isPending } =
    useActiveInvocationCount(datasetId);

  const handleCancel = React.useCallback(async () => {
    if (datasetId === undefined) {
      return;
    }

    try {
      await cancelInvocationsMutation.mutateAsync({ datasetId });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      ErrorMonitoringService.captureError(err, { datasetId });
    }
  }, [datasetId, cancelInvocationsMutation]);

  // Don't render the button if there are no active invocations
  if (datasetId === undefined || isPending || activeCount === 0) {
    return null;
  }

  return (
    <>
      <RobotoButton
        eventName="CancelAssociatedInvocations"
        eventProperties={{
          datasetId,
        }}
        variant="outlined"
        color="warning"
        size="small"
        startIcon={<CancelIcon />}
        onClick={() => setAlertDialogOpen(true)}
        disabled={cancelInvocationsMutation.isPending}
        loading={cancelInvocationsMutation.isPending}
      >
        Cancel All
      </RobotoButton>

      <AlertDialog
        dialogOpen={alertDialogOpen}
        handleClose={() => setAlertDialogOpen(false)}
        dialogTitle="Cancel All Associated Invocations"
        dialogText="Are you sure you want to cancel all active invocations associated with this dataset?"
        dialogAction={handleCancel}
      />
    </>
  );
}
